import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { AutoComplete, Button, DatePicker, Form, Input, Select } from 'antd';
import dayjs from 'dayjs';
import { cn } from '@/libs/utils';
import { DATE_FORMATS } from '@/utils/formUtils';
import {
  buildCountryOptions,
  countryNameForDocument,
  PAID_FOOD_TYPES,
  TRANSPORT_TYPES,
  TRAVEL_ABROAD_CURRENCIES,
  TRAVEL_DOMESTIC_CURRENCY,
  TRAVEL_REASON_KEYS,
} from './constants';

/**
 * First wizard step — the travel order itself.
 *
 * Rates and advances are the employer's call, so they stay read-only for an
 * employee filing their own trip; everything else about the journey is theirs
 * to describe.
 */
export default function TravelOrderStep({
  travelOrder,
  disabled,
  isEmployee,
  isPending,
  onSubmit,
}) {
  const { t, i18n } = useTranslation(['officialTravel']);
  const [form] = Form.useForm();

  // Country names come from the runtime in the viewer's language, so the list
  // is rebuilt when the language changes rather than shipped per language.
  const countryOptions = useMemo(
    () => buildCountryOptions(i18n.language),
    [i18n.language]
  );

  const reasonOptions = useMemo(
    () =>
      TRAVEL_REASON_KEYS.map((key) => ({
        value: t(`options.reason.${key}`, { ns: 'officialTravel' }),
      })),
    [t]
  );

  const abroadCurrency =
    Form.useWatch(['travelOrder', 'dailyWageForAbroadCurrency'], form) ||
    TRAVEL_ABROAD_CURRENCIES[0];

  const transportOptions = useMemo(
    () =>
      TRANSPORT_TYPES.map((value) => ({
        value,
        label: t(`options.transport.${value}`, { ns: 'officialTravel' }),
      })),
    [t]
  );

  const foodOptions = useMemo(
    () =>
      PAID_FOOD_TYPES.map((value) => ({
        value,
        label: t(`options.food.${value}`, { ns: 'officialTravel' }),
      })),
    [t]
  );

  const accommodationOptions = useMemo(
    () => [
      { value: true, label: t('options.yes', { ns: 'officialTravel' }) },
      { value: false, label: t('options.no', { ns: 'officialTravel' }) },
    ],
    [t]
  );

  const currencyOptions = useMemo(
    () => TRAVEL_ABROAD_CURRENCIES.map((value) => ({ value, label: value })),
    []
  );

  useEffect(() => {
    // Switching from an existing order to a new request leaves this component
    // mounted, so without clearing it the previous order's values stay on
    // screen and would be saved as if they were a new one.
    if (!travelOrder) {
      form.resetFields();
      return;
    }

    form.setFieldsValue({
      travelOrder: {
        travelOrderNo: travelOrder.travelOrderNo || '',
        year: travelOrder.year ? dayjs(travelOrder.year) : dayjs(),
        reason: travelOrder.reason || '',
        // Older orders carry only the free-text name; leaving the picker empty
        // in that case is better than guessing which country it meant.
        countryCode: travelOrder.countryCode || undefined,
        city: travelOrder.city || '',
        startDate: travelOrder.startDate ? dayjs(travelOrder.startDate) : null,
        numberOfDays: travelOrder.numberOfDays || null,
        transportType: travelOrder.transportType?.length
          ? travelOrder.transportType
          : [],
        paidAccommodation: Boolean(travelOrder.paidAccommodation),
        paidFood: travelOrder.paidFood || undefined,
        dailyWageReductionPercent: travelOrder.dailyWageReductionPercent || 0,
        paymentInAdvanceCountry: travelOrder.paymentInAdvanceCountry || null,
        paymentInAdvanceAbroad: travelOrder.paymentInAdvanceAbroad || null,
        dailyWageForCountry: travelOrder.dailyWageForCountry || null,
        dailyWageForAbroad: travelOrder.dailyWageForAbroad || null,
        dailyWageForAbroadCurrency:
          travelOrder.dailyWageForAbroadCurrency ||
          TRAVEL_ABROAD_CURRENCIES[0],
        dateOfSubmission: travelOrder.dateOfSubmission
          ? dayjs(travelOrder.dateOfSubmission)
          : dayjs(),
      },
    });
  }, [form, travelOrder]);

  const handleFinish = (values) => {
    const order = values.travelOrder || {};

    onSubmit({
      ...order,
      // The document is issued in Serbian, so the country is stored under its
      // Serbian name whatever language the picker was used in. The code is kept
      // alongside it so the picker can show the right entry next time.
      country: order.countryCode
        ? countryNameForDocument(order.countryCode)
        : travelOrder?.country || '',
      // The picker hands back dayjs objects; the request stores plain dates so
      // both the settlement and the document read the same strings back.
      year: order.year ? order.year.format('YYYY') : '',
      startDate: order.startDate
        ? order.startDate.format(DATE_FORMATS.STANDARD)
        : '',
      dateOfSubmission: order.dateOfSubmission
        ? order.dateOfSubmission.format(DATE_FORMATS.STANDARD)
        : '',
      numberOfDays: Number(order.numberOfDays) || 0,
      paymentInAdvanceCountry: Number(order.paymentInAdvanceCountry) || 0,
      paymentInAdvanceAbroad: Number(order.paymentInAdvanceAbroad) || 0,
      dailyWageForCountry: Number(order.dailyWageForCountry) || 0,
      dailyWageForAbroad: Number(order.dailyWageForAbroad) || 0,
      dailyWageReductionPercent: Math.min(
        Math.max(Number(order.dailyWageReductionPercent) || 0, 0),
        100
      ),
      // Kept in step with the abroad daily wage so the document never prints
      // an advance in one currency and a rate in another.
      paymentInAdvanceAbroadCurrency: order.dailyWageForAbroadCurrency || '',
    });
  };

  // Money the employer sets: an employee may see it but never edit it.
  const employerFieldsDisabled = disabled || isEmployee;

  return (
    <Form
      form={form}
      layout="vertical"
      autoComplete="off"
      onFinish={handleFinish}
      initialValues={{
        travelOrder: {
          year: dayjs(),
          dateOfSubmission: dayjs(),
          paidAccommodation: false,
          transportType: [],
          dailyWageForAbroadCurrency: TRAVEL_ABROAD_CURRENCIES[0],
        },
      }}
      className={cn('form grid gap-4')}
    >
      <div className="rounded-md border bg-zinc-100 p-4">
        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            name={['travelOrder', 'travelOrderNo']}
            label={t('order.travelOrderNo', { ns: 'officialTravel' })}
          >
            <Input disabled={employerFieldsDisabled} />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'year']}
            label={t('order.year', { ns: 'officialTravel' })}
          >
            <DatePicker
              picker="year"
              format="YYYY"
              className="w-full"
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'city']}
            label={t('order.city', { ns: 'officialTravel' })}
            rules={[
              {
                required: true,
                message: t('validation.cityRequired', { ns: 'officialTravel' }),
              },
            ]}
          >
            <Input disabled={disabled} />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'countryCode']}
            label={t('order.country', { ns: 'officialTravel' })}
          >
            <Select
              showSearch
              allowClear
              className="w-full"
              options={countryOptions}
              disabled={disabled}
              placeholder={t('order.countryPlaceholder', {
                ns: 'officialTravel',
              })}
              // Match on the visible name, not the code, so typing "Nem" finds
              // Nemačka the way the user reads it.
              filterOption={(input, option) =>
                option.label.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'reason']}
            label={t('order.reason', { ns: 'officialTravel' })}
          >
            <AutoComplete
              options={reasonOptions}
              disabled={disabled}
              placeholder={t('order.reasonPlaceholder', {
                ns: 'officialTravel',
              })}
              filterOption={(input, option) =>
                option.value.toLowerCase().includes(input.toLowerCase())
              }
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'startDate']}
            label={t('order.startDate', { ns: 'officialTravel' })}
            rules={[
              {
                required: true,
                message: t('validation.startDateRequired', {
                  ns: 'officialTravel',
                }),
              },
            ]}
          >
            <DatePicker
              format={DATE_FORMATS.DISPLAY}
              className="w-full"
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'numberOfDays']}
            label={t('order.numberOfDays', { ns: 'officialTravel' })}
          >
            <Input type="number" min={0} disabled={disabled} />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'transportType']}
            label={t('order.transportType', { ns: 'officialTravel' })}
          >
            <Select
              mode="multiple"
              className="w-full"
              options={transportOptions}
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'paidAccommodation']}
            label={t('order.paidAccommodation', { ns: 'officialTravel' })}
          >
            <Select
              className="w-full"
              options={accommodationOptions}
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'paidFood']}
            label={t('order.paidFood', { ns: 'officialTravel' })}
          >
            <Select
              className="w-full"
              options={foodOptions}
              disabled={disabled}
              allowClear
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'dailyWageReductionPercent']}
            label={t('order.dailyWageReductionPercent', {
              ns: 'officialTravel',
            })}
            tooltip={t('order.dailyWageReductionHint', {
              ns: 'officialTravel',
            })}
          >
            <Input
              type="number"
              step="any"
              min={0}
              max={100}
              suffix="%"
              disabled={employerFieldsDisabled}
            />
          </Form.Item>
        </div>
      </div>

      <div className="rounded-md border bg-zinc-100 p-4">
        <div className="grid gap-x-4 md:grid-cols-2">
          <Form.Item
            name={['travelOrder', 'dailyWageForAbroadCurrency']}
            label={t('order.dailyWageForAbroadCurrency', {
              ns: 'officialTravel',
            })}
          >
            <Select
              showSearch
              className="w-full"
              options={currencyOptions}
              disabled={employerFieldsDisabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'dateOfSubmission']}
            label={t('order.dateOfSubmission', { ns: 'officialTravel' })}
          >
            <DatePicker
              format={DATE_FORMATS.DISPLAY}
              className="w-full"
              disabled={disabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'dailyWageForCountry']}
            label={t('order.dailyWageForCountry', { ns: 'officialTravel' })}
          >
            <Input
              type="number"
              step="any"
              min={0}
              suffix={TRAVEL_DOMESTIC_CURRENCY}
              disabled={employerFieldsDisabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'dailyWageForAbroad']}
            label={t('order.dailyWageForAbroad', { ns: 'officialTravel' })}
          >
            <Input
              type="number"
              step="any"
              min={0}
              suffix={abroadCurrency}
              disabled={employerFieldsDisabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'paymentInAdvanceCountry']}
            label={t('order.paymentInAdvanceCountry', { ns: 'officialTravel' })}
          >
            <Input
              type="number"
              step="any"
              min={0}
              suffix={TRAVEL_DOMESTIC_CURRENCY}
              disabled={employerFieldsDisabled}
            />
          </Form.Item>

          <Form.Item
            name={['travelOrder', 'paymentInAdvanceAbroad']}
            label={t('order.paymentInAdvanceAbroad', { ns: 'officialTravel' })}
          >
            <Input
              type="number"
              step="any"
              min={0}
              suffix={abroadCurrency}
              disabled={employerFieldsDisabled}
            />
          </Form.Item>
        </div>
      </div>

      {!disabled && (
        <div className="form-footer">
          <Button
            shape="round"
            type="primary"
            htmlType="submit"
            className="btn-primary"
            disabled={isPending}
            loading={isPending}
          >
            {t('actions.saveOrder', { ns: 'officialTravel' })}
          </Button>
        </div>
      )}
    </Form>
  );
}
